import { REFERRAL } from 'constants/referral';
import { ROUTES } from 'constants/routes';

interface ReferralParams {
  campaign: string;
  promoterId: string;
}

/**
 * Reads campaign and promoter_id from the current URL (query params).
 */
export const getReferralParamsFromUrl = (): ReferralParams => {
  const params = new URLSearchParams(window.location.search);
  const campaign = params.get(REFERRAL.CAMPAIGN_PARAM)?.trim() ?? '';
  const promoterId = params.get(REFERRAL.PROMOTER_ID_PARAMETER)?.trim() ?? '';
  return { campaign, promoterId };
};

/**
 * Saves referral params to sessionStorage for use during registration.
 */
export const saveReferralParamsToSession = ({ campaign, promoterId }: ReferralParams): void => {
  if (campaign) {
    sessionStorage.setItem(REFERRAL.SESSION_KEYS.CAMPAIGN, campaign);
  }
  if (promoterId) {
    sessionStorage.setItem(REFERRAL.SESSION_KEYS.PROMOTER_ID, promoterId);
  }
};

/**
 * Gets campaign and promoterId stored in sessionStorage (from the referral flow).
 */
export const getReferralParamsFromSession = (): ReferralParams => {
  const campaign = sessionStorage.getItem(REFERRAL.SESSION_KEYS.CAMPAIGN) ?? '';
  const promoterId = sessionStorage.getItem(REFERRAL.SESSION_KEYS.PROMOTER_ID) ?? '';
  return { campaign, promoterId };
};

/**
 * Removes referral params from sessionStorage (after successful registration).
 */
export const clearReferralParamsFromSession = (): void => {
  sessionStorage.removeItem(REFERRAL.SESSION_KEYS.CAMPAIGN);
  sessionStorage.removeItem(REFERRAL.SESSION_KEYS.PROMOTER_ID);
};

/**
 * Returns whether the URL contains referral params.
 */
export const hasReferralParamsInUrl = (): boolean => {
  const { campaign, promoterId } = getReferralParamsFromUrl();
  return Boolean(campaign || promoterId);
};

interface BuildReferralLinkParams {
  promoterId?: string;
  origin?: string;
}

export const buildReferralLink = ({
  promoterId,
  origin = window.location.origin,
}: BuildReferralLinkParams): string =>
  `${origin}${ROUTES.REGISTER}?${REFERRAL.CAMPAIGN_PARAM}=${encodeURIComponent(
    REFERRAL.DEFAULT_CAMPAIGN_REFERRALS
  )}&${REFERRAL.PROMOTER_ID_PARAMETER}=${encodeURIComponent(promoterId ?? '')}`;
